/**
 * Backend-persisted Task Drafts (CU-123yxuaptmf) — replaces the old
 * browser-localStorage store. Follows src/queries/tasks.js's template.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { request, ReqMethods } from "../lib/apiClient";

const { GET, POST, PUT, DELETE } = ReqMethods;

export const taskDraftsKeys = {
  list: (locationId) => ["taskDrafts", locationId],
};

// request() already unwraps to the backend body ({success, data}) — not the
// raw axios envelope — so this reads response.data directly. Exported and
// unit-tested on its own: this exact shape mismatch (one `.data` too many)
// previously made the list silently render empty despite a 200 full of rows.
export const selectTaskDrafts = (response) => response?.data || [];

// The app's default staleTime is 30s (lib/queryClient.js) — fine for most
// data, but these writes happen on a different page than the one reading
// the list (PromptModal vs. the Drafts page / the AppNav badge), so nothing
// would ever tell that cached list it's stale. Every write below takes an
// optional queryClient and invalidates the list on success so a revisit
// within that window refetches instead of silently showing the old list.
const invalidateTaskDrafts = (queryClient, locationId) => {
  if (queryClient && locationId) {
    queryClient.invalidateQueries({ queryKey: taskDraftsKeys.list(locationId) });
  }
};

// GET /task-drafts?location_id=...
export function useTaskDrafts(locationId) {
  return useQuery({
    queryKey: taskDraftsKeys.list(locationId),
    queryFn: () =>
      request(GET, "/task-drafts", { query: `location_id=${locationId}` }),
    enabled: Boolean(locationId),
    select: selectTaskDrafts,
  });
}

// POST /task-drafts — fire-and-forget capture, used outside React Query's
// own render cycle (event handlers, effect cleanups) as well as from here.
export function saveTaskDraft({ locationId, userId, title, prompt, parentKey, parentTitle }, queryClient) {
  return request(POST, "/task-drafts", {
    payload: { locationId, userId, title, prompt, parentKey, parentTitle },
  }).then((res) => {
    invalidateTaskDrafts(queryClient, locationId);
    return res;
  });
}

// PUT /task-drafts — updates a resumed "Unfinished Tasks" draft's content in
// place by _id, so re-abandoning it after "Open" edits the same row instead
// of leaving the original untouched and creating a second one.
export function updateTaskDraft({ id, locationId, title, prompt }, queryClient) {
  return request(PUT, "/task-drafts", {
    payload: { _id: id, locationId, title, prompt },
  }).then((res) => {
    invalidateTaskDrafts(queryClient, locationId);
    return res;
  });
}

// Plain (non-hook) delete by _id — used right after a successful save when
// resuming a specific draft, so it's removed exactly, not by content match.
export function removeTaskDraftById({ id, locationId }, queryClient) {
  if (!id || !locationId) return Promise.resolve();
  return request(DELETE, "/task-drafts", {
    query: `_id=${id}&location_id=${locationId}`,
  })
    .then((res) => {
      invalidateTaskDrafts(queryClient, locationId);
      return res;
    })
    .catch(() => {});
}

// Best-effort cleanup for a Task that just got saved: if it matches an
// "Unfinished Tasks" draft by content (not every save goes through that
// draft's own "Open" button, which already deletes it by _id), remove the
// now-stale draft so a saved Task is never also left sitting in Drafts.
export function removeUnfinishedDraftMatching({ locationId, title, prompt }, queryClient) {
  if (!locationId) return Promise.resolve();
  const query = new URLSearchParams({
    location_id: locationId,
    title: title || "",
    prompt: prompt || "",
  }).toString();
  return request(DELETE, "/task-drafts", { query })
    .then((res) => {
      invalidateTaskDrafts(queryClient, locationId);
      return res;
    })
    .catch(() => {
      // No matching draft (the common case) is not an error worth surfacing.
    });
}

export function useDeleteTaskDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, locationId }) =>
      request(DELETE, "/task-drafts", {
        query: `_id=${id}&location_id=${locationId}`,
      }),
    onSuccess: (_data, { locationId }) => {
      queryClient.invalidateQueries({ queryKey: taskDraftsKeys.list(locationId) });
    },
  });
}
